/**
 * @file frontend/src/lib/api.js
 * @module lib
 * @component api
 *
 * Api — Shared library / API client code.
 *
 * Responsibilities:
 *   - Render the primary UI for this surface.
 *   - Coordinate API calls and local state.
 *   - Emit user actions to parent components / context where applicable.
 *
 * Notes:
 *   - All API calls go through the shared axios client (REACT_APP_BACKEND_URL).
 *   - Side effects belong in `useEffect`; keep render pure.
 */
import axios from 'axios';
import { captureException } from '@sentry/react';

const _raw = process.env.REACT_APP_BACKEND_URL || '';
const BACKEND_URL = _raw && !/^https?:\/\//i.test(_raw) ? `https://${_raw}` : _raw;

export const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
  // P1 hardening (Feb 2026): send the HttpOnly refresh cookie on /api/auth/refresh
  // and other same-origin auth endpoints. Backend issues `k12erp_refresh` cookie on login.
  withCredentials: true,
});

// Request interceptor to add auth token.
// NOTE: tokens are stored in sessionStorage (not sessionStorage) to reduce XSS
// persistence. The proper long-term fix is HttpOnly cookies set by the backend
// (the k12erp_refresh cookie is already issued; access tokens should follow).
api.interceptors.request.use(
  (config) => {
    const token = sessionStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = sessionStorage.getItem('refresh_token');
        const response = await axios.post(
          `${BACKEND_URL}/api/auth/refresh/`,
          { refresh_token: refreshToken },
          { withCredentials: true },
        );

        const { access_token, refresh_token: newRefresh } = response.data;
        sessionStorage.setItem('access_token', access_token);
        if (newRefresh) sessionStorage.setItem('refresh_token', newRefresh);

        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
        window.location.href = '/login';
      }
    }

    if (error.response?.status >= 500) {
      const safeUrl = (error.config?.url || '')
        .replace(/\/[0-9a-f-]{8,}/gi, '/:id')
        .replace(/\/\d+/g, '/:id');
      captureException(error, {
        extra: {
          url: safeUrl,
          method: error.config?.method,
          status: error.response?.status,
        },
      });
    }

    return Promise.reject(error);
  }
);

/**
 * Fetch every page of a list endpoint and return the flattened results array.
 *
 * Handles both response shapes this API uses:
 *   - a bare array              : returned as-is (endpoint isn't paginated)
 *   - { count, next, results }  : DRF pagination; all pages are accumulated
 *                                 by following `next` until it is null.
 *
 * The `next` link is an absolute URL whose host can differ from the configured
 * backend, so we don't fetch it directly. Instead we re-issue the same `url`
 * against this client (preserving auth + baseURL), carrying only `next`'s query
 * params. That also keeps this agnostic to page-number vs cursor schemes.
 */
export const fetchAllPages = async (url, config = {}) => {
  const first = await api.get(url, config);
  const data = first.data;
  if (Array.isArray(data)) return data;                  // unpaginated list
  if (!data || !Array.isArray(data.results)) return [];  // unexpected shape

  const results = [...data.results];
  let next = data.next;
  let guard = 0;
  while (next && guard < 100) {                           // hard stop ~2000 rows
    guard += 1;
    const params = Object.fromEntries(
      new URL(next, window.location.origin).searchParams.entries()
    );
    const res = await api.get(url, { ...config, params: { ...(config.params || {}), ...params } });
    const page = res.data;
    if (Array.isArray(page)) { results.push(...page); break; }
    if (Array.isArray(page?.results)) results.push(...page.results);
    next = page?.next;
  }
  if (guard >= 100) console.warn(`fetchAllPages: hit 100-page cap for ${url}; results may be truncated`);
  return results;
};

export default api;
